import {
  FONT_SIZE,
  FONT_WEIGHT,
  LINE_HEIGHT,
  SPACING,
  PADDING,
  MARGIN,
  GAP,
  COMPONENT_SIZES,
  Z_INDEX,
  SHADOWS,
  TRANSITION_TIMING,
  TRANSITION_EASING,
  TRANSITIONS,
  ANIMATION_TIMING,
  ANIMATIONS,
  BREAKPOINTS_VALUES,
  MEDIA_QUERIES,
  ICON_SIZES,
  ICON_STROKE_WIDTH,
} from "../definitions";

const CUSTOM_THEME_COLORS = {
  primary: "#f5eadb",
  secondary: "#7d6b5a",
  accent: "#d69d7e",
  warning: "#c97c2b",
  discount: "#c48a6a",

  white: "#ffffff",
  black: "#1a120b",

  greyLight: "#efe5d8",
  greyMedium: "#9a8b7a",
  greyDark: "#6d5c4c",
  greyExtraDark: "#4d3f33",

  success: "#4f9d69",
  error: "#c5483b",
  info: "#5b8fb9",

  overlay: {
    dark: "#01010133",
    darkStrong: "#01010166",
    light: "#01010199",
    veryLight: "#01010108",
  },

  text: {
    primary: "#2b2118",
    secondary: "#6e5b4a",
    disabled: "#cbb9a7",
    light: "rgba(43, 33, 24, 0.70)",
    lighter: "rgba(43, 33, 24, 0.60)",
  },

  background: {
    paper: "#f5eadb",
    default: "#f5eadb",
  },
} as const;

const CUSTOM_FONT_FAMILY = {
  primary: "'Inter', 'Rubik', sans-serif",
  rubik: "'Inter', 'Rubik', sans-serif",
  sans: "'Inter', 'Rubik', sans-serif",
} as const;

const CUSTOM_TYPOGRAPHY_STYLES = {
  rubik: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  rubikSmall: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.sm,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  rubikMedium: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.base,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  rubikLarge: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.lg,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  rubikXL: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.xl,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  rubik2XL: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE["2xl"],
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  rubik3XL: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE["3xl"],
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  heading1: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.h1,
    fontWeight: FONT_WEIGHT.bold,
    lineHeight: LINE_HEIGHT.tight,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  heading2: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.h2,
    fontWeight: FONT_WEIGHT.bold,
    lineHeight: LINE_HEIGHT.tight,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  heading3: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.h3,
    fontWeight: FONT_WEIGHT.bold,
    lineHeight: LINE_HEIGHT.tight,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  body: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.base,
    lineHeight: LINE_HEIGHT.normal,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  bodySmall: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.sm,
    lineHeight: LINE_HEIGHT.normal,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  bodyLarge: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.lg,
    lineHeight: LINE_HEIGHT.normal,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  caption: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.xs,
    lineHeight: LINE_HEIGHT.tight,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  label: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.sm,
    fontWeight: FONT_WEIGHT.bold,
    textTransform: "uppercase" as const,
    lineHeight: LINE_HEIGHT.normal,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  labelMedium: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.base,
    fontWeight: FONT_WEIGHT.bold,
    textTransform: "uppercase" as const,
    color: CUSTOM_THEME_COLORS.text.primary,
  },

  helperText: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.xs,
    fontStyle: "italic" as const,
    color: "rgba(43, 33, 24, 0.6)",
  },

  button: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.base,
    fontWeight: FONT_WEIGHT.bold,
    textTransform: "uppercase" as const,
    lineHeight: LINE_HEIGHT.tight,
  },

  buttonSmall: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.sm,
    fontWeight: FONT_WEIGHT.bold,
    textTransform: "uppercase" as const,
  },

  display: {
    fontFamily: CUSTOM_FONT_FAMILY.primary,
    fontSize: FONT_SIZE.display,
    fontWeight: FONT_WEIGHT.bold,
    lineHeight: LINE_HEIGHT.tight,
  },
} as const;

const CUSTOM_SPACING = {
  ...SPACING,
  base: 10,
  0.5: "5px",
  1: "10px",
  1.5: "15px",
  2: "20px",
  2.5: "25px",
  3: "30px",
  3.5: "35px",
  3.75: "38px",
  4: "40px",
  5: "50px",
  6: "60px",
  8: "80px",
  10: "100px",
  12: "120px",
  16: "160px",
  20: "200px",
  24: "240px",
} as const;

const CUSTOM_PADDING = {
  ...PADDING,
  xs: "6px",
  sm: "10px",
  md: "18px",
  lg: "28px",
  xl: "36px",
  "2xl": "56px",
} as const;

const CUSTOM_MARGIN = {
  ...MARGIN,
  xs: "6px",
  sm: "10px",
  md: "18px",
  lg: "28px",
  xl: "36px",
  "2xl": "56px",
} as const;

const CUSTOM_GAP = {
  ...GAP,
  xs: "6px",
  sm: "10px",
  md: "18px",
  lg: "28px",
  xl: "36px",
} as const;

const CUSTOM_BORDERS = {
  none: "none",
  thin: "1px solid",
  thick: "2px solid",

  light: `1px solid ${CUSTOM_THEME_COLORS.greyLight}`,
  medium: `1px solid ${CUSTOM_THEME_COLORS.greyMedium}`,
  dark: `1px solid ${CUSTOM_THEME_COLORS.greyExtraDark}`,
  lightOverlay: "1px solid rgba(26, 18, 11, 0.08)",
} as const;

const CUSTOM_BORDER_RADIUS = {
  none: "0px",
  sm: "4px",
  base: "6px",
  md: "10px",
  lg: "14px",
  xl: "18px",
  "2xl": "24px",
  full: "9999px",
} as const;

const CUSTOM_ELEVATION_STYLES = {
  level1: {
    backgroundColor: CUSTOM_THEME_COLORS.background.paper,
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.sm,
  },

  level2: {
    backgroundColor: CUSTOM_THEME_COLORS.background.paper,
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.base,
  },

  level3: {
    backgroundColor: CUSTOM_THEME_COLORS.background.paper,
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.md,
  },

  card: {
    backgroundColor: CUSTOM_THEME_COLORS.background.paper,
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.base,
    borderRadius: CUSTOM_BORDER_RADIUS.md,
  },

  modal: {
    backgroundColor: CUSTOM_THEME_COLORS.background.paper,
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.xl,
  },

  white: {
    backgroundColor: CUSTOM_THEME_COLORS.background.paper,
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.base,
  },
} as const;

const CUSTOM_BUTTON_BORDER_STYLES = {
  bottomOnly: {
    border: "none",
    borderBottom: CUSTOM_BORDERS.thin + " " + CUSTOM_THEME_COLORS.secondary,
  },

  withShadow: {
    border: CUSTOM_BORDERS.light,
    boxShadow: SHADOWS.sm,
  },
} as const;

export const THEME_CUSTOM = {
  name: "custom",
  label: "Custom",

  colors: CUSTOM_THEME_COLORS,

  typography: {
    fontFamily: CUSTOM_FONT_FAMILY,
    fontSize: FONT_SIZE,
    fontWeight: FONT_WEIGHT,
    lineHeight: LINE_HEIGHT,
    styles: CUSTOM_TYPOGRAPHY_STYLES,
  },

  spacing: {
    values: CUSTOM_SPACING,
    padding: CUSTOM_PADDING,
    margin: CUSTOM_MARGIN,
    gap: CUSTOM_GAP,
  },

  components: {
    sizes: COMPONENT_SIZES,
    zIndex: Z_INDEX,
  },

  visual: {
    shadows: SHADOWS,
    borders: CUSTOM_BORDERS,
    borderRadius: CUSTOM_BORDER_RADIUS,
    elevation: CUSTOM_ELEVATION_STYLES,
    buttonBorders: CUSTOM_BUTTON_BORDER_STYLES,
  },

  animation: {
    timing: TRANSITION_TIMING,
    easing: TRANSITION_EASING,
    transitions: TRANSITIONS,
    animationTiming: ANIMATION_TIMING,
    animations: ANIMATIONS,
  },

  responsive: {
    breakpoints: BREAKPOINTS_VALUES,
    mediaQueries: MEDIA_QUERIES,
  },

  icons: {
    sizes: ICON_SIZES,
    strokeWidth: ICON_STROKE_WIDTH,
  },
} as const;

export type ThemeCustom = typeof THEME_CUSTOM;














